import { Box, Button, IconButton, InputAdornment, Link, ListItemAvatar, ListItemSecondaryAction, makeStyles, TextField, Theme, Typography } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { AttachFile, BugReport, Delete, MoreHoriz, Person } from '@material-ui/icons';
import LogRocket from 'logrocket'
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom'

import { Collections } from 'Collections';
import { useAppUser } from 'components/AppUserProvider';
import { FullscreenLoading } from 'components/FullscreenLoading';
import { useOrg } from 'components/OrgProvider';
import { useOrgUser } from 'components/OrgUserProvider';
import { PageNavigation } from 'components/PageNavigation';
import { persistedDb, storage } from 'FirebaseConfigs';
import { FirebaseUtils } from 'firebaseUtils/FirebaseUtils';
import { AnalyticsEvent } from 'models/AnalyticsEvent';
import { AppRoles } from 'models/AppRoles';
import { noAppUserNameText } from 'models/AppUser';
import { avatarImageFrom } from 'models/AvatarImage';
import { HelpChat } from 'models/HelpChat';
import { Message, MessageAttachment } from 'models/Message';
import { Nullable } from 'models/Nullable';
import { textValueFrom } from 'models/TextValue';
import { Paths } from 'Paths';
import { dateTimeFormat } from 'Util/date.utils';
import { handleEnterKey } from 'Util/handleEnterKey';

import { MenuButton } from './ListScreen';

type Props = {
    isOwn?: boolean
    userId?: string
}


function fileListToArray(files: FileList): File[] {
    return Array.from(Array(files.length).keys())
        .map(index => files.item(index))
        .filter(file => !!file) as File[]
}


const useStyles = makeStyles((theme: Theme) => ({
    avatar: {
        marginTop: theme.spacing(1),
        marginBottom: 'auto',
    },
    menu: {
        top: theme.spacing(4),
    },
}))

export function HelpMessages({ isOwn, userId }: Props) {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const [org, loadingOrg] = useOrg()
    const [orgUserDoc, loadingOrgUser] = useOrgUser()
    const [appUserDoc, loadingAppUser] = useAppUser()
    const [message, setMessage] = useState('')
    const [attachments, setAttachments] = useState<File[] | null>(null)
    const [uploading, setUploading] = useState(false)

    const orgUser = orgUserDoc?.data || null
    const appUser = appUserDoc?.data || null

    const isAdmin = !!appUser?.appRoles.map[AppRoles.FlatEnum.ADMIN]

    const [helpChat, loadingHelpChat] = FirebaseUtils.useDocumentData<HelpChat>(
        true,
        Collections.HELP_CHATS,
        (isOwn ? appUser?.id : userId) ?? null,
        AnalyticsEvent.GET_HELP_MESSAGES_ERROR,
    )

    const messagesCollection = appUser ?
        persistedDb
            .collection(Collections.HELP_CHATS)
            .doc(isOwn ? appUser.id : userId)
            .collection(Collections.HELP_MESSAGES)
        : null

    const [messages, loadingMessages] = FirebaseUtils.useCollectionData<Message>(
        true,
        messagesCollection,
        AnalyticsEvent.GET_HELP_MESSAGES_ERROR,
        {},
        (query, path) => (
            appUser && query.orderBy(path.metadata.createdAt, 'asc')
        ))

    const loading = loadingMessages || loadingOrgUser || loadingOrg || loadingAppUser || loadingHelpChat

    const inputRef = useRef<any>()
    const lastMessageRef = useRef<any>()

    async function sendMessage() {
        if (appUser && messagesCollection && !uploading && (message || attachments?.length)) {
            const chatId = appUser.id
            if (!helpChat) {
                FirebaseUtils.createDocument<HelpChat>(
                    true,
                    Collections.HELP_CHATS,
                    chatId,
                    appUser,
                    orgUser,
                    AnalyticsEvent.CREATED_HELP_CHAT,
                    AnalyticsEvent.CREATED_HELP_CHAT,
                    AnalyticsEvent.SEND_HELP_MESSAGE_ERROR,
                    enqueueSnackbar,
                    null,
                    {
                        appUser,
                        orgUser,
                        org: org?.data || null,
                    },
                )
            }
            const messageId = messagesCollection.doc().id
            const attachedFiles: Nullable<Array<MessageAttachment>> = (
                attachments?.length
                && await Promise.all(attachments.map<Promise<MessageAttachment>>(async (file) => {
                    const attachmentDoc = persistedDb.collection(Collections.HELP_MESSAGE_ATTACHMENTS).doc()
                    const attachmentPath = 'HelpChats/' + chatId + '/HelpMessages/' + messageId + '/Attachments/' + attachmentDoc.id
                    const attachmentFileRef = storage.ref().child(attachmentPath)
                    if (!uploading) {
                        setUploading(true)
                    }
                    await attachmentFileRef.put(file)
                        .then(() => enqueueSnackbar('Lastet opp ' + file.name, { variant: 'success' }))
                        .catch((e) => {
                            enqueueSnackbar(e.message, { variant: 'error' })
                            attachmentFileRef.delete()
                        })
                    const url: string = await attachmentFileRef.getDownloadURL()

                    const attachmentData: MessageAttachment = {
                        id: attachmentDoc.id,
                        name: textValueFrom(file.name, true),
                        path: attachmentPath,
                        userId: appUser.id,
                        content: avatarImageFrom(url, true),
                        messageId,
                        contentType: file.type,
                    }

                    FirebaseUtils.createDocument<MessageAttachment>(
                        true,
                        Collections.HELP_MESSAGE_ATTACHMENTS,
                        attachmentDoc.id,
                        appUser,
                        orgUser,
                        AnalyticsEvent.CREATED_HELP_MESSAGE_ATTACHMENT,
                        AnalyticsEvent.CREATED_HELP_MESSAGE_ATTACHMENT,
                        AnalyticsEvent.CREATE_HELP_MESSAGE_ATTACHMENT_ERROR,
                        enqueueSnackbar,
                        null,
                        attachmentData,
                    )

                    return attachmentData
                })).finally(() => setUploading(false)))
                || null

            const sessionUrl = LogRocket.sessionURL

            FirebaseUtils.createDocument<Message>(
                true,
                messagesCollection,
                messageId,
                appUser,
                orgUser,
                AnalyticsEvent.CREATED_HELP_MESSAGE,
                AnalyticsEvent.CREATED_HELP_MESSAGE,
                AnalyticsEvent.SEND_HELP_MESSAGE_ERROR,
                enqueueSnackbar,
                null,
                {
                    text: textValueFrom(message),
                    userId: appUser.id,
                    attachments: (attachedFiles?.filter(attachment => !!attachment) as Nullable<Array<MessageAttachment>>) || null,
                    analytics: sessionUrl ? {
                        logRocket: {
                            sessionUrl,
                        },
                    } : null,
                },
            )
            setMessage('')
            setAttachments(null)
            inputRef.current?.focus?.()
        }
    }

    const content = loading ? <FullscreenLoading screenId="Help" /> : (
        <List dense>
            {messages?.map((message, index) => {
                return (
                    <ListItem key={message.data.id} ref={(index === (messages.length - 1)) ? lastMessageRef : undefined}>
                        <ListItemAvatar
                            className={classes.avatar}>
                            <Avatar
                                component={RouterLink}
                                to={Paths.APP_USERS + '/' + message.metadata.createdBy.appUser?.id}
                                src={message.metadata.createdBy.appUser?.avatarImage?.original.url || undefined}>
                                <Person />
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={noAppUserNameText(message.metadata.createdBy.appUser?.contactInfo?.name?.fullName) + ' - ' + dateTimeFormat.format(message.metadata.createdAt.toDate())}
                            secondary={(
                                <div>
                                    <Typography variant="body1" color="textPrimary" style={{ width: '100%', wordWrap: 'break-word' }}>{message.data.text?.text}</Typography>
                                    {message.data.attachments?.map((attachment) => {
                                        if (attachment.contentType.includes('image')) {
                                            return (
                                                <Link
                                                    target="_blank"
                                                    key={attachment.id}
                                                    href={attachment.content.original.url}
                                                >
                                                    <img
                                                        style={{ maxWidth: '80%', maxHeight: '60vh' }}
                                                        src={attachment.content.original.url}
                                                        alt={attachment.name.text}
                                                    />
                                                </Link>
                                            )
                                        }
                                        return (
                                            <Link key={attachment.id} target="_blank" display="block" gutterBottom style={{ wordWrap: 'break-word' }} href={attachment.content.original.url}>{attachment.name.text}</Link>
                                        )
                                    })}
                                </div>
                            )}
                        />
                        <ListItemSecondaryAction className={classes.menu}>
                            {(isAdmin && message.data.analytics?.logRocket.sessionUrl) ? (
                                <IconButton component={Link} href={message.data.analytics?.logRocket.sessionUrl} target="_blank">
                                    <BugReport />
                                </IconButton>
                            ) : null}
                            <MenuButton
                                label="Alternativer"
                                icon={<MoreHoriz />}
                                options={[
                                    (!(messagesCollection && appUser)) ? null : ["Slett", <Delete />, () => {
                                        FirebaseUtils.deleteDocument(
                                            true,
                                            messagesCollection,
                                            message.data.id,
                                            appUser,
                                            orgUser,
                                            AnalyticsEvent.DELETED_HELP_MESSAGE,
                                            AnalyticsEvent.DELETED_HELP_MESSAGE,
                                            AnalyticsEvent.DELETE_HELP_MESSAGE_ERROR,
                                            enqueueSnackbar,
                                            'Slettet meldingen',
                                        )
                                    }]
                                ]}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                )
            })}
            {messages?.length ? null : (
                <ListItem>
                    <ListItemText
                        primary="Du har ingen meldinger enda"
                        secondary="Send en melding for å kontakte utviklerene av appen"
                    />
                </ListItem>
            )}
            <ListItem>
                <div style={{ height: '100px' }} />
            </ListItem>
        </List>
    )

    useEffect(() => {
        lastMessageRef.current?.scrollIntoView?.({ behavior: 'smooth' })
    }, [lastMessageRef, messages])

    return (
        <PageNavigation
            noExtraBottomMargin
            backHistoryButton
            title="Hjelp/Tilbakemelding"
            bottomAppBar={(
                <Box mx={1} my={1}>
                    {attachments?.length ? (
                        <List dense>
                            {
                                attachments.map((file, index) => (
                                    <ListItem key={file.name}>
                                        <ListItemText primary={file.name} />
                                        <ListItemSecondaryAction>
                                            <IconButton color="secondary" onClick={() => setAttachments(attachments.filter((_, i) => i !== index))}>
                                                <Delete />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))
                            }
                        </List>
                    ) : null}
                    <TextField
                        type="text"
                        variant="outlined"
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        placeholder="Skriv en melding til utviklerne ..."
                        fullWidth
                        autoFocus
                        ref={inputRef}
                        onKeyPress={handleEnterKey(sendMessage)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size="small" component="label">
                                        <AttachFile />
                                        <input
                                            multiple
                                            type="file"
                                            hidden
                                            // If value is not specified, the internal value will be the files that were previously selected, preventing previous selections from being selected again
                                            value=""
                                            onChange={(e) => {
                                                const newAttachments = e.target.files && fileListToArray(e.target.files)
                                                setAttachments([
                                                    ...(attachments || []),
                                                    ...(newAttachments || []),
                                                ])
                                            }}
                                        />
                                    </IconButton>
                                    <Button disabled={!(appUser && messagesCollection && !uploading && (message || attachments?.length))} variant="contained" color="primary" onClick={sendMessage}>Send</Button>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            )}
        >
            {content}
        </PageNavigation>
    )
}
